import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  courseAPI,
  lessonAPI,
  scopeSequenceAPI,
  teacherAPI,
  unitAPI,
  unitGuideAPI,
} from '../services/api';
import { getGuideByCourseAndUnit } from '../data/unitGuides';
import TEKSModal from '../components/TEKSModal';
import ScopeSequenceViewer from '../components/ScopeSequenceViewer';
import './GuidePage.css';

const toSafeDate = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const raw = String(value).trim();
  if (!raw) return null;
  const cleaned = raw.replace(/\s*\([^)]*\)\s*$/, '');
  if (!cleaned) return null;

  const ymd = cleaned.includes('T') ? cleaned.split('T')[0] : cleaned;
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(cleaned);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const formatTeacherDate = (value) => {
  const parsed = toSafeDate(value);
  if (!parsed) return null;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTeacherDateRange = (start, end) => {
  const formattedStart = formatTeacherDate(start);
  const formattedEnd = formatTeacherDate(end);

  if (formattedStart && formattedEnd) return `${formattedStart} to ${formattedEnd}`;
  return formattedStart || formattedEnd || null;
};

const formatGuideDateRange = (rawRange) => {
  if (!rawRange) return null;
  const value = String(rawRange).trim();
  if (!value) return null;

  const delimiters = [/\s+to\s+/i, /\s+[–-]\s+/];
  for (const delimiter of delimiters) {
    const parts = value.split(delimiter).map((part) => part.trim());
    if (parts.length === 2) {
      const formatted = formatTeacherDateRange(parts[0], parts[1]);
      if (formatted) return formatted;
    }
  }

  return formatTeacherDate(value);
};

const toAssetUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.PUBLIC_URL || '';
  return `${base}${url}`;
};

const GuidePage = ({ courseId, onOpenLesson }) => {
  const [units, setUnits] = useState([]);
  const [courseName, setCourseName] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [lessons, setLessons] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [selectedTeksCode, setSelectedTeksCode] = useState(null);
  const [pedagogyByLessonId, setPedagogyByLessonId] = useState({});
  const [scopeSequenceEntries, setScopeSequenceEntries] = useState([]);
  const [dbGuide, setDbGuide] = useState(null);
  const [teacherObjectiveOverride, setTeacherObjectiveOverride] = useState(null);
  const [isEditingObjectives, setIsEditingObjectives] = useState(false);
  const [objectiveDraftText, setObjectiveDraftText] = useState('');
  const [savingObjectiveOverride, setSavingObjectiveOverride] = useState(false);
  const [objectiveOverrideError, setObjectiveOverrideError] = useState('');

  useEffect(() => {
    if (!courseId) {
      setUnits([]);
      setCourseName('');
      setSelectedUnitId('');
      setLessons([]);
      setScopeSequenceEntries([]);
      return;
    }

    const loadUnits = async () => {
      setLoadingUnits(true);
      try {
        const response = await unitAPI.getAll(courseId);
        const nextUnits = response.data || [];
        setUnits(nextUnits);

        const unit1 = nextUnits.find((unit) => Number(unit.unit_number) === 1);
        setSelectedUnitId(unit1?.id || nextUnits[0]?.id || '');
      } catch (error) {
        console.error('Error loading units for guide page:', error);
      } finally {
        setLoadingUnits(false);
      }
    };

    loadUnits();
  }, [courseId]);

  useEffect(() => {
    if (!courseId) {
      setScopeSequenceEntries([]);
      return;
    }

    let cancelled = false;
    const loadScopeSequence = async () => {
      try {
        const response = await scopeSequenceAPI.getAll(courseId);
        if (!cancelled) {
          setScopeSequenceEntries(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error loading scope sequence for guide page:', error);
        if (!cancelled) setScopeSequenceEntries([]);
      }
    };

    loadScopeSequence();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    if (!courseId) {
      setCourseName('');
      return;
    }

    const loadCourse = async () => {
      try {
        const response = await courseAPI.getById(courseId);
        setCourseName(response.data?.name || '');
      } catch (error) {
        console.error('Error loading course for guide page:', error);
        setCourseName('');
      }
    };

    loadCourse();
  }, [courseId]);

  useEffect(() => {
    if (!selectedUnitId) {
      setLessons([]);
      setPedagogyByLessonId({});
      return;
    }

    const loadLessons = async () => {
      setLoadingLessons(true);
      try {
        const response = await lessonAPI.getAll({
          unitId: selectedUnitId,
          includePedagogy: true,
        });
        const lessonRows = response.data || [];
        setLessons(lessonRows);

        const pedagogyMap = {};
        lessonRows.forEach((lesson) => {
          const hasPedagogy =
            lesson.misconceptions
            || lesson.academic_vocabulary_prior
            || lesson.academic_vocabulary_lesson
            || lesson.academic_vocabulary_supporting;
          pedagogyMap[lesson.id] = hasPedagogy
            ? {
                misconceptions: lesson.misconceptions || "",
                academic_vocabulary_prior: lesson.academic_vocabulary_prior || "",
                academic_vocabulary_lesson: lesson.academic_vocabulary_lesson || "",
                academic_vocabulary_supporting: lesson.academic_vocabulary_supporting || "",
                is_unit_specific: lesson.is_unit_specific === true,
                unit_override_notes: lesson.unit_override_notes || null,
                unit_override_source_label: lesson.unit_override_source_label || null,
              }
            : null;
        });
        setPedagogyByLessonId(pedagogyMap);
      } catch (error) {
        console.error('Error loading lessons for guide page:', error);
        setPedagogyByLessonId({});
      } finally {
        setLoadingLessons(false);
      }
    };

    loadLessons();
  }, [selectedUnitId]);

  const selectedUnit = useMemo(
    () => units.find((unit) => unit.id === selectedUnitId) || null,
    [selectedUnitId, units],
  );

  useEffect(() => {
    if (!courseId || !selectedUnit?.unit_number) {
      setDbGuide(null);
      return;
    }

    let cancelled = false;
    const loadDbGuide = async () => {
      setDbGuide(null);
      setLoadingGuide(true);
      try {
        const response = await unitGuideAPI.getByUnit(courseId, selectedUnit.unit_number);
        if (!cancelled) {
          setDbGuide(response.data || null);
        }
      } catch (error) {
        console.error('Error loading unit guide from database:', error);
        if (!cancelled) setDbGuide(null);
      } finally {
        if (!cancelled) setLoadingGuide(false);
      }
    };

    loadDbGuide();
    return () => {
      cancelled = true;
    };
  }, [courseId, selectedUnit?.unit_number]);

  useEffect(() => {
    if (!courseId || !selectedUnit?.unit_number) {
      setTeacherObjectiveOverride(null);
      return;
    }

    let cancelled = false;
    const loadTeacherOverride = async () => {
      try {
        const response = await teacherAPI.getUnitObjectiveOverride(
          courseId,
          selectedUnit.unit_number,
        );
        if (!cancelled) setTeacherObjectiveOverride(response.data || null);
      } catch (error) {
        console.error('Error loading teacher objective override:', error);
        if (!cancelled) setTeacherObjectiveOverride(null);
      }
    };

    loadTeacherOverride();
    return () => {
      cancelled = true;
    };
  }, [courseId, selectedUnit?.unit_number]);

  const guide = useMemo(
    () =>
      dbGuide ||
      getGuideByCourseAndUnit(courseName, selectedUnit?.unit_number),
    [courseName, dbGuide, selectedUnit?.unit_number],
  );

  const teacherObjectiveBullets = useMemo(() => {
    const raw = teacherObjectiveOverride?.learning_objectives;
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => String(item || '').trim()).filter(Boolean);
  }, [teacherObjectiveOverride?.learning_objectives]);

  const hasTeacherObjectiveOverride = useMemo(
    () => Array.isArray(teacherObjectiveOverride?.learning_objectives),
    [teacherObjectiveOverride?.learning_objectives],
  );

  useEffect(() => {
    setObjectiveDraftText(teacherObjectiveBullets.join('\n'));
    setIsEditingObjectives(false);
    setObjectiveOverrideError('');
  }, [teacherObjectiveBullets, selectedUnit?.unit_number]);

  const saveTeacherObjectives = useCallback(async () => {
    if (!courseId || !selectedUnit?.unit_number) return;

    const nextObjectives = objectiveDraftText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    setSavingObjectiveOverride(true);
    setObjectiveOverrideError('');
    try {
      const response = await teacherAPI.upsertUnitObjectiveOverride({
        courseId,
        unitNumber: selectedUnit.unit_number,
        learningObjectives: nextObjectives,
      });
      setTeacherObjectiveOverride(response.data || null);
      setIsEditingObjectives(false);
    } catch (error) {
      console.error('Error saving teacher objective override:', error);
      setObjectiveOverrideError('Unable to save your objective override.');
    } finally {
      setSavingObjectiveOverride(false);
    }
  }, [courseId, objectiveDraftText, selectedUnit?.unit_number]);

  const selectedScopeEntry = useMemo(() => {
    if (!selectedUnit) return null;
    const matching = scopeSequenceEntries
      .filter((entry) => Number(entry.unit_number) === Number(selectedUnit.unit_number))
      .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')));
    return matching[0] || null;
  }, [scopeSequenceEntries, selectedUnit]);

  const sourceLinks = useMemo(() => {
    const seen = new Set();
    const links = [];

    const maybeAdd = (title, url) => {
      if (!url) return;
      const resolved = toAssetUrl(url);
      if (seen.has(resolved)) return;
      seen.add(resolved);
      links.push({ title: title || 'Source document', url: resolved });
    };

    maybeAdd(guide?.sourceLabel, guide?.sourceUrl);
    maybeAdd(
      selectedScopeEntry?.source_document_title,
      selectedScopeEntry?.source_document_url,
    );

    return links;
  }, [guide?.sourceLabel, guide?.sourceUrl, selectedScopeEntry]);

  const dateRangeLabel = useMemo(() => {
    const fromGuideStructured = formatTeacherDateRange(
      guide?.startDate,
      guide?.endDate,
    );
    if (fromGuideStructured) return fromGuideStructured;

    const fromGuide = formatGuideDateRange(guide?.dateRange);
    if (fromGuide) return fromGuide;

    const fromUnit = formatTeacherDateRange(
      selectedUnit?.start_date,
      selectedUnit?.end_date,
    );
    return fromUnit || 'See scope and sequence';
  }, [guide?.dateRange, guide?.endDate, guide?.startDate, selectedUnit?.start_date, selectedUnit?.end_date]);

  const totalDaysLabel = useMemo(() => {
    if (guide?.totalDays) return guide.totalDays;
    if (typeof selectedScopeEntry?.ab_day_count === 'number') {
      return `${selectedScopeEntry.ab_day_count} A/B days`;
    }
    return 'See scope and sequence';
  }, [guide?.totalDays, selectedScopeEntry?.ab_day_count]);

  useEffect(() => {
    if (!guide?.transcriptPath) {
      setTranscript('Transcript unavailable.');
      return;
    }

    fetch(toAssetUrl(guide.transcriptPath))
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => setTranscript(text || 'Transcript unavailable.'))
      .catch(() => setTranscript('Transcript unavailable.'));
  }, [guide?.transcriptPath]);

  const lessonsByChapter = useMemo(() => {
    const groups = {};
    lessons.forEach((lesson) => {
      const chapter = lesson.chapter_ref || 'Uncategorized';
      if (!groups[chapter]) groups[chapter] = [];
      groups[chapter].push(lesson);
    });
    return groups;
  }, [lessons]);

  const vocabularySummary = useMemo(() => {
    const splitTerms = (value) =>
      String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

    const prior = new Set();
    const lesson = new Set();
    const supporting = new Set();
    const misconceptions = new Set();

    lessons.forEach((item) => {
      const pedagogy = pedagogyByLessonId[item.id];
      if (!pedagogy) return;

      splitTerms(pedagogy.academic_vocabulary_prior).forEach((term) => prior.add(term));
      splitTerms(pedagogy.academic_vocabulary_lesson).forEach((term) => lesson.add(term));
      splitTerms(pedagogy.academic_vocabulary_supporting).forEach((term) =>
        supporting.add(term),
      );
      if (pedagogy.misconceptions) misconceptions.add(pedagogy.misconceptions);
    });

    return {
      prior: [...prior].sort((a, b) => a.localeCompare(b)),
      lesson: [...lesson].sort((a, b) => a.localeCompare(b)),
      supporting: [...supporting].sort((a, b) => a.localeCompare(b)),
      misconceptions: [...misconceptions],
    };
  }, [lessons, pedagogyByLessonId]);

  const scrollToSection = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openLessonViewer = useCallback(
    (lessonId) => {
      if (!lessonId) return;
      if (typeof onOpenLesson === 'function') {
        onOpenLesson(lessonId);
        return;
      }
      window.location.hash = `/lesson/${lessonId}`;
    },
    [onOpenLesson],
  );

  if (!courseId) {
    return (
      <div className="panel-card">
        <h2>Guide View</h2>
        <p>Select a course to view unit guides.</p>
      </div>
    );
  }

  return (
    <div className="guide-page panel-card" data-tour="guide-root">
      <div className="guide-topbar" data-tour="guide-unit-select">
        <div>
          <h2>Guide View</h2>
          <p>Unit guide-style page with section navigation and full transcript.</p>
        </div>

        <div className="guide-topbar-controls">
          <ScopeSequenceViewer
            courseId={courseId}
            selectedUnitNumber={selectedUnit?.unit_number}
          />
          <div className="guide-select">
            <label htmlFor="unit-select">Unit</label>
            <select
              id="unit-select"
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
              disabled={loadingUnits || units.length === 0}
            >
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  Unit {unit.unit_number}: {unit.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {(loadingUnits || loadingGuide) && <p>Loading unit guide...</p>}
      {!loadingUnits && !selectedUnit && <p>No units found for this course.</p>}

      {!loadingUnits && selectedUnit && (
        <div className="guide-layout">
          <aside className="guide-nav" data-tour="guide-sidebar">
            <h3>Sections</h3>
            {guide?.sections?.map((section) => (
              <button
                key={section.id}
                type="button"
                className="guide-nav-link"
                onClick={() => scrollToSection(section.id)}
              >
                {section.heading}
              </button>
            ))}
            <button
              type="button"
              className="guide-nav-link"
              onClick={() => scrollToSection('lesson-sequence')}
            >
              Lesson Sequence
            </button>
          </aside>

          <main className="guide-content">
            <section className="guide-hero" data-tour="guide-hero">
              <h1>{guide?.title || `Unit ${selectedUnit.unit_number}: ${selectedUnit.title}`}</h1>
              <p>{guide?.subtitle || 'Unit Guide'}</p>
              {hasTeacherObjectiveOverride && (
                <p>
                  <strong>View Mode:</strong> Your personal learning-objective overrides are active.
                </p>
              )}
              <p>
                <strong>Date Range:</strong> {dateRangeLabel}
              </p>
              <p>
                <strong>Total Days:</strong> {totalDaysLabel}
              </p>
              {sourceLinks.length > 0 && (
                <p>
                  <strong>Source:</strong>{' '}
                  {sourceLinks.map((source, idx) => (
                    <React.Fragment key={source.url}>
                      {idx > 0 && ' | '}
                      <a href={source.url} target="_blank" rel="noreferrer">
                        {source.title}
                      </a>
                    </React.Fragment>
                  ))}
                </p>
              )}
              {sourceLinks.length === 0 && guide?.sourceLabel && (
                <p>
                  <strong>Source:</strong> {guide.sourceLabel}
                </p>
              )}
            </section>

            {guide?.sections?.map((section) => (
              (() => {
                const sectionId = String(section.id || '').toLowerCase();
                const sectionHeading = String(section.heading || '').toLowerCase();
                const isLearningObjectivesSection =
                  (sectionId.includes('learning') && sectionId.includes('objective'))
                  || sectionHeading.includes('learning objective');
                const sectionBullets = isLearningObjectivesSection && hasTeacherObjectiveOverride
                  ? teacherObjectiveBullets
                  : section.bullets;

                return (
              <section
                key={section.id}
                id={section.id}
                className="guide-section"
                data-tour={section.id === 'vocab' ? 'guide-vocab' : undefined}
              >
                <h3>{section.heading}</h3>
                {section.paragraphs?.map((text, idx) => (
                  <p key={`${section.id}-p-${idx}`}>{text}</p>
                ))}
                {sectionBullets?.length > 0 && (
                  <ul>
                    {sectionBullets.map((item, idx) => (
                      <li key={`${section.id}-b-${idx}`}>{item}</li>
                    ))}
                  </ul>
                )}
                {isLearningObjectivesSection && hasTeacherObjectiveOverride && (
                  <p className="muted">These learning objectives are your personal version.</p>
                )}
                {isLearningObjectivesSection && (
                  <div className="guide-objective-editor">
                    {!isEditingObjectives && (
                      <button
                        type="button"
                        className="nav-lite-btn"
                        onClick={() => {
                          const source = hasTeacherObjectiveOverride
                            ? teacherObjectiveBullets
                            : (sectionBullets || []);
                          setObjectiveDraftText(source.join('\n'));
                          setIsEditingObjectives(true);
                          setObjectiveOverrideError('');
                        }}
                      >
                        Customize My Learning Objectives
                      </button>
                    )}
                    {isEditingObjectives && (
                      <div className="field-group">
                        <label htmlFor="teacher-objective-draft" className="field-label">
                          One objective per line
                        </label>
                        <textarea
                          id="teacher-objective-draft"
                          value={objectiveDraftText}
                          onChange={(event) => setObjectiveDraftText(event.target.value)}
                          rows={6}
                        />
                        <div className="planner-header-actions">
                          <button
                            type="button"
                            className="primary-btn"
                            onClick={saveTeacherObjectives}
                            disabled={savingObjectiveOverride}
                          >
                            {savingObjectiveOverride ? 'Saving...' : 'Save My Objectives'}
                          </button>
                          <button
                            type="button"
                            className="nav-lite-btn"
                            onClick={() => {
                              setObjectiveDraftText(teacherObjectiveBullets.join('\\n'));
                              setIsEditingObjectives(false);
                              setObjectiveOverrideError('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                        {objectiveOverrideError && <p className="muted">{objectiveOverrideError}</p>}
                      </div>
                    )}
                  </div>
                )}
                {section.id === 'vocab' && (
                  <div className="vocab-wired">
                    {loadingLessons && <p>Loading vocabulary and clarifications from lesson pedagogy...</p>}
                    {!loadingLessons && (
                      <>
                        {(vocabularySummary.prior.length > 0 ||
                          vocabularySummary.lesson.length > 0 ||
                          vocabularySummary.supporting.length > 0) ? (
                          <div className="vocab-grid">
                            <div className="vocab-card">
                              <h4>Prior Knowledge Terms</h4>
                              {vocabularySummary.prior.length > 0 ? (
                                <ul>
                                  {vocabularySummary.prior.map((term) => (
                                    <li key={`prior-${term}`}>{term}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p>No prior vocabulary records in pedagogy table for this unit.</p>
                              )}
                            </div>

                            <div className="vocab-card">
                              <h4>Lesson Vocabulary</h4>
                              {vocabularySummary.lesson.length > 0 ? (
                                <ul>
                                  {vocabularySummary.lesson.map((term) => (
                                    <li key={`lesson-${term}`}>{term}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p>No lesson vocabulary records in pedagogy table for this unit.</p>
                              )}
                            </div>

                            <div className="vocab-card">
                              <h4>Supporting Vocabulary</h4>
                              {vocabularySummary.supporting.length > 0 ? (
                                <ul>
                                  {vocabularySummary.supporting.map((term) => (
                                    <li key={`support-${term}`}>{term}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p>No supporting vocabulary records in pedagogy table for this unit.</p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p>
                            No structured vocabulary fields were found in lesson pedagogy for this unit yet.
                            Use the full transcript section for complete chapter vocabulary until those rows are seeded.
                          </p>
                        )}

                        {vocabularySummary.misconceptions.length > 0 && (
                          <div className="vocab-card misconceptions-card">
                            <h4>Clarifications / Common Misconceptions</h4>
                            <ul>
                              {vocabularySummary.misconceptions.map((item, idx) => (
                                <li key={`mis-${idx}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {section.id === 'full-transcript' && (
                  <details>
                    <summary>Open full transcript</summary>
                    <pre className="guide-transcript">{transcript}</pre>
                  </details>
                )}
              </section>
                );
              })()
            ))}

            <section id="lesson-sequence" className="guide-section" data-tour="guide-lesson-sequence">
              <h3>Lesson Sequence (Database)</h3>
              {loadingLessons && <p>Loading lessons...</p>}
              {!loadingLessons && lessons.length === 0 && (
                <p>No lessons found yet for this unit.</p>
              )}

              {!loadingLessons && lessons.length > 0 && (
                <div className="chapter-groups">
                  {Object.keys(lessonsByChapter)
                    .sort((a, b) => a.localeCompare(b))
                    .map((chapter) => (
                      <div key={chapter} className="chapter-card">
                        <h4>{chapter}</h4>
                        {lessonsByChapter[chapter].map((lesson) => (
                          <div key={lesson.id} className="lesson-row">
                          <div>
                              <strong>{lesson.lesson_title}</strong>
                              {lesson.essential_questions && (
                                <p>{lesson.essential_questions}</p>
                              )}
                            </div>
                            <div className="lesson-row-actions">
                              <button
                                type="button"
                                className="lesson-open-btn"
                                onClick={() => openLessonViewer(lesson.id)}
                                aria-label={`Open lesson viewer for ${lesson.lesson_title}`}
                              >
                                Open Lesson Viewer
                              </button>
                              <button
                                type="button"
                                className="teks-chip"
                                onClick={() => setSelectedTeksCode(lesson.teks_code)}
                                title={
                                  lesson.teks_description
                                    ? `TEKS ${lesson.teks_code}: ${lesson.teks_description}`
                                    : `TEKS ${lesson.teks_code}`
                                }
                              >
                                TEKS {lesson.teks_code}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </section>
          </main>
        </div>
      )}

      {selectedTeksCode && (
        <TEKSModal
          teksCode={selectedTeksCode}
          courseId={courseId}
          onClose={() => setSelectedTeksCode(null)}
        />
      )}
    </div>
  );
};

export default GuidePage;
